import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useTranslations } from '@/hooks/use-translations'
import { useInstagramInsightsQuery } from '@/queries/meta'

const ACCOUNT_METRICS = ['reach', 'views', 'profile_views', 'accounts_engaged', 'total_interactions'] as const
const MEDIA_METRICS = ['reach', 'views', 'likes', 'comments', 'shares', 'saved'] as const

function formatNumber(value: number | null | undefined) {
  return typeof value === 'number' ? value.toLocaleString() : '—'
}

function Stat({ label, value, title }: { label: string; value: number | null | undefined; title?: string }) {
  return (
    <div className="space-y-1" title={title}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold tracking-tight">{formatNumber(value)}</p>
    </div>
  )
}

export function InstagramInsightsTab() {
  const { t } = useTranslations()
  const insights = useInstagramInsightsQuery(true)

  if (insights.isLoading) return <Skeleton className="h-64 w-full" />

  if (insights.error || !insights.data) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
        {t('instagram_page.load_error')}: {insights.error?.message}
      </div>
    )
  }

  const { profile, metrics, errors, period_days: days, media } = insights.data

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <Stat label={t('instagram_page.insights.followers')} value={profile.followers_count} />
          <Stat label={t('instagram_page.insights.follows')} value={profile.follows_count} />
          <Stat label={t('instagram_page.insights.posts')} value={profile.media_count} />
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">{t('instagram_page.insights.period', { days })}</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {ACCOUNT_METRICS.map((metric) => (
            <Stat
              key={metric}
              label={t(`instagram_page.insights.${metric}`)}
              value={metrics[metric]}
              title={errors[metric] ? t('instagram_page.insights.metric_unavailable') : undefined}
            />
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">{t('instagram_page.insights.posts_title')}</h2>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('instagram_page.insights.post')}</TableHead>
                {MEDIA_METRICS.map((metric) => (
                  <TableHead key={metric} className="text-right">{t(`instagram_page.insights.${metric}`)}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {media.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="max-w-xs">
                    <a href={item.permalink} target="_blank" rel="noreferrer" className="line-clamp-2 text-sm hover:underline">
                      {item.caption || item.id}
                    </a>
                    <span className="text-xs text-muted-foreground">
                      {item.timestamp ? new Date(item.timestamp).toLocaleDateString() : ''}
                    </span>
                  </TableCell>
                  {MEDIA_METRICS.map((metric) => (
                    <TableCell key={metric} className="text-right">{formatNumber(item.insights?.[metric])}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  )
}
